import {
  useOmniOfferShowQuery,
  useOmniOfferUpdateStateMutation,
} from "@/services/backend/omni/offer/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { NavLink, useNavigate, useParams } from "react-router";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { OmniBreadcrumbs } from "../../_components/omni-breadcrumbs";
import { Offer } from "@/services/backend/omni/offer/types/offer";
import { OfferPdf } from "@/services/backend/omni/offer/components/offer-pdf";
import { OfferDeleteDialog } from "@/services/backend/omni/offer/components/offer-delete-dialog";

export function OfferRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useOmniOfferShowQuery({
    id: offerId!,
  });

  const breadcrumbs = offer && (
    <OmniBreadcrumbs customerId={offer.customerId} processId={offer.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/omni/${offer.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={offer.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </OmniBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {offer && <OfferPage offer={offer} />}
    </ProcessPageMainTemplate>
  );
}

function OfferPage(props: { offer: Offer }) {
  const { offer } = props;

  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <Page offer={offer} />
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

// The field names of each tab: the keys of offer.ReadyForCustomer and of
// the validation errors of the fields shown on the tab.
const links = [
  {
    label: t("Basisdaten"),
    to: "data",
    fieldName: [
      "referenceNumber",
      "date",
      "representative",
      "companyAddress",
      "customerId",
      "customerAddress",
      "branches",
      "serviceObjects",
    ],
  },
  {
    label: t("Preise"),
    to: "pricing",
    fieldName: ["pricing", "pricingBy"],
  },
  {
    label: t("Leistungen"),
    to: "scope-of-works",
    fieldName: ["scopeOfWork"],
  },
  {
    label: t("Dateien"),
    to: "files",
  },
  {
    label: t("Verträge"),
    to: "contracts",
  },
];

function Page({ offer }: { offer: Offer }) {
  const navigate = useNavigate();

  return (
    <DocumentPage>
      <DocumentStateAlert document={offer} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(offer) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <OfferDeleteDialog
                offerId={offer.id}
                onDeleted={() =>
                  navigate(`/processes/omni/${offer.processId}/offers`)
                }
              />
            </>
          )}
          <DocumentStateDropdown
            document={offer}
            mutation={useOmniOfferUpdateStateMutation}
          />
          <OfferPdf offer={offer} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
